import { useEffect, useRef, useState } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";

export type ChatSearchFilter = "all" | "from" | "has" | "mentions";

// Structural subset of ChatLayout's Msg — only what search results render/filter.
export type ChatSearchMsg = {
  id: string;
  authorName?: string;
  content?: string;
  attachments?: unknown[];
  createdAt?: string;
};

type UseChatSearchOptions = {
  currentVoidId: string;
  currentChannelId: string;
};

/**
 * In-channel message search plus the jump-to-message state it drives
 * (scrollToId/highlightId consumed by the virtualized list, inContextMode for
 * the "viewing old context" banner). All of it resets together when the
 * conversation changes, which is why it lives in one hook.
 */
export function useChatSearch({ currentVoidId, currentChannelId }: UseChatSearchOptions) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ChatSearchFilter>("all");
  const [fromUser, setFromUser] = useState("");
  const [results, setResults] = useState<ChatSearchMsg[]>([]);
  const [loading, setLoading] = useState(false);
  const [scrollToId, setScrollToId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [inContextMode, setInContextMode] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Monotonic request id so a slow earlier search can't apply its results over
  // a newer one (the old AbortController was never wired to api.getAuth, so its
  // abort was a no-op — fast typing could race a stale response in).
  const reqIdRef = useRef(0);

  // Clear search + scroll state when switching conversations
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setOpen(false);
    setQuery("");
    setFilter("all");
    setScrollToId(null);
    setHighlightId(null);
    setInContextMode(false);
  }, [currentVoidId]);

  // Reset scrollToId after Virtuoso consumes it so it won't retrigger
  useEffect(() => {
    if (!scrollToId) return;
    const t = setTimeout(() => setScrollToId(null), 500);
    return () => clearTimeout(t);
  }, [scrollToId]);

  // Off-click closes the search dropdown
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // API-backed search — fires when query or filter changes
  useEffect(() => {
    // Bump first so any in-flight request from a prior run is invalidated —
    // including when this run early-returns (e.g. the user cleared the query).
    const myReqId = ++reqIdRef.current;
    const q = query.trim();
    // Don't search until there's something meaningful to look for
    const needsQuery = filter === "all" || filter === "from";
    if (needsQuery && !q) { setResults([]); return; }
    const channelFqid = currentVoidId && currentChannelId
      ? (currentChannelId.includes(":") ? currentChannelId : `${currentVoidId}:${currentChannelId}`)
      : "";
    if (!channelFqid) return;
    const tok = getAuthToken() || "";
    setLoading(true);
    // Build the backend search string per filter type
    let searchQ: string;
    if (filter === "has") searchQ = "http";
    else if (filter === "mentions") searchQ = q ? `@${q}` : "@";
    else searchQ = q; // 'all' or 'from'
    api.getAuth(`/channels/search?channelId=${encodeURIComponent(channelFqid)}&q=${encodeURIComponent(searchQ)}`, tok)
      .then((res: any) => {
        // Ignore a stale response that resolved after a newer query started
        if (myReqId !== reqIdRef.current) return;
        let msgs: ChatSearchMsg[] = Array.isArray(res?.messages) ? res.messages : [];
        // Additional client-side filtering
        if (filter === "from") msgs = msgs.filter(m => (m.authorName || "").toLowerCase().includes(q.toLowerCase()));
        if (filter === "has") msgs = msgs.filter(m => (m.attachments?.length ?? 0) > 0 || /https?:\/\//.test(m.content || ""));
        setResults(msgs.reverse()); // chronological: oldest first → newest last
      })
      .catch(() => {})
      .finally(() => {
        // Only the latest in-flight request controls the spinner
        if (myReqId === reqIdRef.current) setLoading(false);
      });
  }, [query, filter, currentVoidId, currentChannelId]);

  return {
    open, setOpen,
    query, setQuery,
    filter, setFilter,
    fromUser, setFromUser,
    results, loading,
    scrollToId, setScrollToId,
    highlightId, setHighlightId,
    inContextMode, setInContextMode,
    containerRef,
  };
}


